import { mathEditingPluginKey } from '@/components/tiptap-editor/tiptap-extension/math-extension/math-editing-extension';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Card, CardBody } from '@/components/tiptap-editor/tiptap-ui-primitive/card';
import { useFloatingElement } from '@/hooks/use-floating-element';
import { startCommentDraft, useCommentDraft } from '@/lib/comment-draft-store';
import { getSelectionBoundingRect } from '@/lib/tiptap-collab-utils';
import { flip, offset, shift } from '@floating-ui/react';
import { isNodeSelection, type Editor } from '@tiptap/react';
import { MessageSquarePlus } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

const FLOATING_Z_INDEX = 1000;
const HOVER_CLOSE_DELAY_MS = 200;
const MATH_NODE_TYPES = new Set(['inlineMath', 'blockMath']);

type HoverMathContext = {
    pos: number;
    anchorRect: DOMRect;
};

type CommentBubbleShouldShowProps = {
    editor: Editor;
    element: HTMLElement;
    from: number;
    to: number;
};

function isMathBeingEdited(editor: Editor) {
    return mathEditingPluginKey.getState(editor.state)?.pendingEdit != null;
}

/**
 * Unlike the default bubble menu check, this does not require an editable editor: reviewers can
 * comment while the document is read-only, for example during a diff or while paused.
 *
 * Shows for non-empty text selections and for NodeSelection (e.g. animations and other
 * CommonNodeViewWrapper nodes), which have no textBetween content when they are atoms.
 */
export function shouldShowCommentBubble({ editor, element, from, to }: CommentBubbleShouldShowProps): boolean {
    if (!('setThread' in editor.commands) || editor.state.selection.empty) {
        return false;
    }
    if (element.contains(document.activeElement)) {
        return canCommentOnSelection(editor, from, to);
    }

    const nodeSelected = isNodeSelection(editor.state.selection);

    if (editor.isEditable) {
        if (!editor.view.hasFocus()) {
            return false;
        }
    } else if (!nodeSelected) {
        // A read-only editor cannot take focus, so check that the user's text selection is inside it.
        // NodeSelection has no DOM text range; editor state is enough in that case.
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed || !selection.anchorNode || !editor.view.dom.contains(selection.anchorNode)) {
            return false;
        }
    }

    return canCommentOnSelection(editor, from, to);
}

function canCommentOnSelection(editor: Editor, from: number, to: number): boolean {
    if (isNodeSelection(editor.state.selection)) {
        return true;
    }
    return editor.state.doc.textBetween(from, to).trim().length > 0;
}

function getMathHoverContext(editor: Editor, target: EventTarget | null): HoverMathContext | null {
    if (!(target instanceof Element) || !editor.isEditable || isMathBeingEdited(editor)) {
        return null;
    }

    const mathEl = target.closest('.tiptap-mathematics-render');
    if (!(mathEl instanceof HTMLElement) || !editor.view.dom.contains(mathEl)) {
        return null;
    }

    const pos = editor.view.posAtDOM(mathEl, 0);
    const node = editor.state.doc.nodeAt(pos);
    if (!node || !MATH_NODE_TYPES.has(node.type.name)) {
        return null;
    }

    return {
        pos,
        anchorRect: mathEl.getBoundingClientRect(),
    };
}

function getMathAnchorRect(editor: Editor, pos: number): DOMRect | null {
    const dom = editor.view.nodeDOM(pos);
    if (dom instanceof Element) {
        return dom.getBoundingClientRect();
    }
    return null;
}

function rectSnapshotKey(rect: DOMRect | null): string {
    if (!rect) {
        return 'null';
    }
    return `${rect.top},${rect.left},${rect.width},${rect.height}`;
}

/** A button above selected text (or hovered/selected math) that starts a comment on it. */
export function CommentBubble({ editor }: { editor: Editor }) {
    const draft = useCommentDraft(editor);
    const [hoverMath, setHoverMath] = useState<HoverMathContext | null>(null);
    const hoverMathRef = useRef<HoverMathContext | null>(null);
    const bubbleRef = useRef<HTMLDivElement | null>(null);
    const isPointerOverPopoverRef = useRef(false);
    const hoverCloseTimerRef = useRef<number | null>(null);
    const selectionRectCacheRef = useRef<{ key: string; rect: DOMRect | null }>({ key: 'null', rect: null });

    const subscribeMathEditing = useCallback(
        (onStoreChange: () => void) => {
            if (!editor || editor.isDestroyed) {
                return () => {};
            }
            editor.on('transaction', onStoreChange);
            return () => {
                editor.off('transaction', onStoreChange);
            };
        },
        [editor],
    );

    const getMathBeingEditedSnapshot = useCallback(() => {
        if (!editor || editor.isDestroyed) {
            return false;
        }
        return isMathBeingEdited(editor);
    }, [editor]);

    const mathBeingEdited = useSyncExternalStore(subscribeMathEditing, getMathBeingEditedSnapshot);

    const subscribeSelectionAnchor = useCallback(
        (onStoreChange: () => void) => {
            if (!editor || editor.isDestroyed || !('setThread' in editor.commands)) {
                return () => {};
            }
            editor.on('selectionUpdate', onStoreChange);
            editor.on('transaction', onStoreChange);
            window.addEventListener('scroll', onStoreChange, { capture: true });
            return () => {
                editor.off('selectionUpdate', onStoreChange);
                editor.off('transaction', onStoreChange);
                window.removeEventListener('scroll', onStoreChange, { capture: true });
            };
        },
        [editor],
    );

    const getSelectionAnchorSnapshot = useCallback(() => {
        if (!editor || editor.isDestroyed || !('setThread' in editor.commands)) {
            selectionRectCacheRef.current = { key: 'null', rect: null };
            return null;
        }
        const el = editor.view.dom as HTMLElement;
        const { from, to } = editor.state.selection;
        const next = shouldShowCommentBubble({ editor, element: el, from, to }) ? getSelectionBoundingRect(editor) : null;
        const key = rectSnapshotKey(next);
        if (selectionRectCacheRef.current.key === key) {
            return selectionRectCacheRef.current.rect;
        }
        selectionRectCacheRef.current = { key, rect: next };
        return next;
    }, [editor]);

    const selectionAnchorRect = useSyncExternalStore(subscribeSelectionAnchor, getSelectionAnchorSnapshot);

    useEffect(() => {
        hoverMathRef.current = hoverMath;
    }, [hoverMath]);

    const clearHoverCloseTimer = useCallback(() => {
        if (hoverCloseTimerRef.current != null) {
            window.clearTimeout(hoverCloseTimerRef.current);
            hoverCloseTimerRef.current = null;
        }
    }, []);

    const clearHoverMath = useCallback(() => {
        clearHoverCloseTimer();
        isPointerOverPopoverRef.current = false;
        setHoverMath(null);
    }, [clearHoverCloseTimer]);

    const scheduleHoverClose = useCallback(() => {
        clearHoverCloseTimer();
        hoverCloseTimerRef.current = window.setTimeout(() => {
            // Portaled bubble can be mid-enter when editor mouseleave fires; check DOM too.
            if (isPointerOverPopoverRef.current || bubbleRef.current?.matches(':hover')) {
                isPointerOverPopoverRef.current = true;
            } else {
                setHoverMath(null);
            }
            hoverCloseTimerRef.current = null;
        }, HOVER_CLOSE_DELAY_MS);
    }, [clearHoverCloseTimer]);

    const setHoverMathIfChanged = useCallback((nextHover: HoverMathContext) => {
        setHoverMath(prev => {
            if (prev && prev.pos === nextHover.pos && rectSnapshotKey(prev.anchorRect) === rectSnapshotKey(nextHover.anchorRect)) {
                return prev;
            }
            return nextHover;
        });
    }, []);

    useEffect(() => {
        if (!editor || editor.isDestroyed || !('setThread' in editor.commands)) {
            return;
        }

        const el = editor.view.dom as HTMLElement;

        const onScroll = () => {
            const currentHover = hoverMathRef.current;
            if (!currentHover) {
                return;
            }
            const nextRect = getMathAnchorRect(editor, currentHover.pos);
            if (!nextRect) {
                clearHoverMath();
                return;
            }
            setHoverMathIfChanged({ ...currentHover, anchorRect: nextRect });
        };

        const onTransaction = () => {
            if (isMathBeingEdited(editor)) {
                clearHoverMath();
            }
        };

        const onMouseMove = (e: MouseEvent) => {
            const nextHover = getMathHoverContext(editor, e.target);
            if (!nextHover) {
                scheduleHoverClose();
                return;
            }
            clearHoverCloseTimer();
            // Pointer is over math in the editor, not the portaled bubble.
            isPointerOverPopoverRef.current = false;
            setHoverMathIfChanged(nextHover);
        };

        const onMouseLeave = (e: MouseEvent) => {
            // Moving into the portaled bubble still leaves the editor DOM; keep the hover open.
            if (e.relatedTarget instanceof Node && bubbleRef.current?.contains(e.relatedTarget)) {
                isPointerOverPopoverRef.current = true;
                clearHoverCloseTimer();
                return;
            }
            scheduleHoverClose();
        };

        el.addEventListener('mousemove', onMouseMove);
        el.addEventListener('mouseleave', onMouseLeave);
        editor.on('transaction', onTransaction);
        window.addEventListener('scroll', onScroll, { capture: true });

        return () => {
            el.removeEventListener('mousemove', onMouseMove);
            el.removeEventListener('mouseleave', onMouseLeave);
            editor.off('transaction', onTransaction);
            window.removeEventListener('scroll', onScroll, { capture: true });
            clearHoverCloseTimer();
        };
    }, [clearHoverCloseTimer, clearHoverMath, editor, scheduleHoverClose, setHoverMathIfChanged]);

    // Prefer hoverMath.anchorRect (new object when pos/geometry changes) so Floating UI repositions
    // between math nodes; setHoverMathIfChanged avoids updating on every mousemove over the same node.
    const activeAnchorRect = hoverMath?.anchorRect ?? selectionAnchorRect;
    const show = Boolean(activeAnchorRect) && !draft && !mathBeingEdited && 'setThread' in editor.commands;

    const { ref, style, getFloatingProps, isMounted } = useFloatingElement(show, activeAnchorRect, FLOATING_Z_INDEX, {
        placement: 'top',
        middleware: [offset(8), flip({ mainAxis: true, crossAxis: false }), shift()],
    });

    const setBubbleRef = useCallback(
        (node: HTMLDivElement | null) => {
            bubbleRef.current = node;
            ref(node);
        },
        [ref],
    );

    const onBubbleMouseEnter = useCallback(() => {
        isPointerOverPopoverRef.current = true;
        clearHoverCloseTimer();
    }, [clearHoverCloseTimer]);

    const onBubbleMouseLeave = useCallback(() => {
        isPointerOverPopoverRef.current = false;
        scheduleHoverClose();
    }, [scheduleHoverClose]);

    const onComment = useCallback(() => {
        if (hoverMath) {
            editor.chain().setNodeSelection(hoverMath.pos).run();
        }
        clearHoverMath();
        startCommentDraft(editor);
    }, [clearHoverMath, editor, hoverMath]);

    if (!isMounted || !show || !activeAnchorRect) {
        return null;
    }

    return createPortal(
        <div
            ref={setBubbleRef}
            style={style}
            {...getFloatingProps()}
            data-testid="comment-bubble"
            onMouseEnter={onBubbleMouseEnter}
            onMouseLeave={onBubbleMouseLeave}
        >
            <Card>
                <CardBody>
                    <Button
                        type="button"
                        data-style="ghost"
                        aria-label="Add comment"
                        tooltip="Add comment"
                        // Keeps the selection so the draft attaches to it.
                        onMouseDown={event => event.preventDefault()}
                        onClick={onComment}
                    >
                        <MessageSquarePlus className="tiptap-button-icon" />
                        <span className="tiptap-button-text">Comment</span>
                    </Button>
                </CardBody>
            </Card>
        </div>,
        document.body,
    );
}
